/**
 * Touch controls for playing in a phone browser.
 *
 * The game was built for keyboard and mouse with pointer lock, none of which
 * exists on iOS Safari: there is no pointer lock API, no scroll wheel, no
 * shift key and no `E`. This module adds a DOM overlay that produces the same
 * inputs the desktop path produces, and nothing here runs at all unless the
 * device actually reports touch — the desktop build is untouched.
 *
 * Pointer Events rather than Touch Events. Safari has supported them since
 * iOS 13, they unify mouse and touch so the layer can be exercised from a
 * desktop browser with `?touch=1` during development, and `setPointerCapture`
 * keeps a drag alive when the finger slides outside the element it started in
 * — which on a joystick is most drags.
 */

/** Analog movement, read once per frame by the game loop. */
export interface TouchState {
  forward: number;
  strafe: number;
  running: boolean;
}

export interface TouchControls {
  readonly state: TouchState;
  /** Hide during the title screen and while a modal owns the screen. */
  setVisible(visible: boolean): void;
  /** Greys the interact button out when there is nothing in reach. */
  setInteractEnabled(enabled: boolean): void;
  /**
   * Stand the layer down while a modal or the pause menu owns the screen.
   *
   * This is not cosmetic. The layer sits above the HUD so that a drag anywhere
   * turns the camera, which also puts it above the phone: left up, its
   * full-screen look zone would swallow every tap meant for the phone's tabs
   * and buttons. Only the menu button stays, because on a phone it is the way
   * back out.
   */
  setModal(open: boolean): void;
  dispose(): void;
}

export interface TouchHooks {
  look(dx: number, dy: number): void;
  zoom(delta: number): void;
  interact(): void;
  phone(): void;
  pause(): void;
  /** Look and joystick drags are ignored while a modal owns the screen. */
  isModalOpen(): boolean;
  /** Any touch is a user gesture, which is what unblocks the audio context. */
  gesture(): void;
}

/**
 * Whether to mount the touch layer at all.
 *
 * `maxTouchPoints` alone is wrong: Windows laptops with touchscreens report
 * it and would lose their mouse controls. The pointer media query is the part
 * that says the *primary* input is a finger. `?touch=1` forces it on so the
 * layer can be developed and smoke-tested in a desktop browser.
 */
export function isTouchPrimary(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (new URLSearchParams(window.location.search).get('touch') === '1') return true;
  } catch { /* a malformed query string is not worth failing over */ }
  return (
    navigator.maxTouchPoints > 0 &&
    window.matchMedia('(pointer: coarse)').matches
  );
}

const STICK_RADIUS = 58;
// Below this the stick is treated as centred. Thumbs rest heavy and a phone
// digitiser jitters; without a dead zone the avatar drifts while standing.
const DEAD_ZONE = 0.16;
// Push the stick past this and the figure runs, so a sprint does not require
// a second thumb. The Run button latches it on for longer journeys.
const RUN_EDGE = 0.92;
// A multiplier on the mouse look scale, NOT an angle: `CameraRig.look` takes
// raw pixel deltas and applies its own 0.0024 rad/px internally, which is what
// the desktop mousemove path passes it. Pre-scaling to radians here made every
// drag about four hundred times too small - the camera moved 0.002 rad on a
// 180px swipe. A finger travels less distance than a mouse before it runs out
// of screen, so it gets a little more than 1:1.
const LOOK_SPEED = 1.35;

export function createTouchControls(
  root: HTMLElement,
  hooks: TouchHooks,
): TouchControls {
  const state: TouchState = { forward: 0, strafe: 0, running: false };
  let runLatched = false;

  const layer = document.createElement('div');
  layer.className = 'le-touch-layer';

  // --- look zone -----------------------------------------------------------
  // Sits underneath everything else and fills the screen, so a drag anywhere
  // that is not a control turns the camera. The controls above it stop their
  // own pointers with `pointerdown` handlers rather than by carving holes in
  // this element, which would leave dead strips between them.
  const lookZone = document.createElement('div');
  lookZone.className = 'le-touch-look';
  layer.appendChild(lookZone);

  // --- stick ---------------------------------------------------------------
  const stick = document.createElement('div');
  stick.className = 'le-touch-stick';
  const knob = document.createElement('i');
  stick.appendChild(knob);
  layer.appendChild(stick);

  // --- buttons -------------------------------------------------------------
  const cluster = document.createElement('div');
  cluster.className = 'le-touch-buttons';

  const interactBtn = button('le-touch-interact', 'E', 'Interact');
  const runBtn = button('le-touch-run', '»', 'Run');
  const phoneBtn = button('le-touch-phone', '▤', 'Phone');
  cluster.append(interactBtn, runBtn, phoneBtn);
  layer.appendChild(cluster);

  const menuBtn = button('le-touch-menu', '⋯', 'Menu');
  layer.appendChild(menuBtn);

  root.appendChild(layer);

  function button(className: string, glyph: string, label: string): HTMLButtonElement {
    const el = document.createElement('button');
    el.className = `le-touch-btn ${className}`;
    el.type = 'button';
    el.setAttribute('aria-label', label);
    el.innerHTML = `<span class="g">${glyph}</span><span class="l">${label}</span>`;
    return el;
  }

  /**
   * Buttons fire on `pointerdown`, not on `click`.
   *
   * Safari delays `click` by up to 300 ms while it waits to see whether the
   * tap becomes a double-tap. On an interact button that delay is the
   * difference between the game feeling responsive and feeling broken.
   */
  function tap(el: HTMLElement, run: () => void): void {
    el.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      hooks.gesture();
      el.classList.add('down');
      run();
    });
    // The release is listened for on the window, not on the button. Pressing
    // Phone opens the phone, which stands the whole cluster down before the
    // finger lifts, so a pointerup bound to the element never arrives and the
    // button is left looking held down for the rest of the session - visible
    // in the first mobile screenshot as a phone button stuck pale.
    const clear = (): void => el.classList.remove('down');
    window.addEventListener('pointerup', clear);
    window.addEventListener('pointercancel', clear);
    el.addEventListener('pointerleave', clear);
  }

  tap(interactBtn, () => hooks.interact());
  tap(phoneBtn, () => hooks.phone());
  tap(menuBtn, () => hooks.pause());
  tap(runBtn, () => {
    runLatched = !runLatched;
    runBtn.classList.toggle('latched', runLatched);
  });

  // --- joystick ------------------------------------------------------------
  let stickPointer: number | null = null;
  let originX = 0;
  let originY = 0;

  stick.addEventListener('pointerdown', (e) => {
    if (hooks.isModalOpen()) return;
    e.preventDefault();
    e.stopPropagation();
    hooks.gesture();
    stickPointer = e.pointerId;
    const box = stick.getBoundingClientRect();
    originX = box.left + box.width / 2;
    originY = box.top + box.height / 2;
    stick.setPointerCapture(e.pointerId);
    stick.classList.add('active');
    moveStick(e.clientX, e.clientY);
  });

  stick.addEventListener('pointermove', (e) => {
    if (e.pointerId !== stickPointer) return;
    e.preventDefault();
    moveStick(e.clientX, e.clientY);
  });

  const endStick = (e: PointerEvent): void => {
    if (e.pointerId !== stickPointer) return;
    stickPointer = null;
    stick.classList.remove('active');
    knob.style.transform = '';
    state.forward = 0;
    state.strafe = 0;
    state.running = runLatched;
  };
  stick.addEventListener('pointerup', endStick);
  stick.addEventListener('pointercancel', endStick);

  function moveStick(x: number, y: number): void {
    let dx = (x - originX) / STICK_RADIUS;
    let dy = (y - originY) / STICK_RADIUS;
    const length = Math.hypot(dx, dy);
    if (length > 1) {
      dx /= length;
      dy /= length;
    }
    const magnitude = Math.min(1, length);
    knob.style.transform = `translate(${dx * STICK_RADIUS}px, ${dy * STICK_RADIUS}px)`;

    if (magnitude < DEAD_ZONE) {
      state.forward = 0;
      state.strafe = 0;
      state.running = runLatched;
      return;
    }
    // Rescale so the axis still reaches full deflection after the dead zone is
    // subtracted; without this the stick can never quite ask for full speed.
    const scale = (magnitude - DEAD_ZONE) / (1 - DEAD_ZONE) / magnitude;
    state.strafe = clamp(dx * scale, -1, 1);
    // Screen-down is world-backward.
    state.forward = clamp(-dy * scale, -1, 1);
    state.running = runLatched || magnitude > RUN_EDGE;
  }

  // --- look and pinch ------------------------------------------------------
  const looks = new Map<number, { x: number; y: number }>();
  let pinchDistance = 0;

  lookZone.addEventListener('pointerdown', (e) => {
    hooks.gesture();
    if (hooks.isModalOpen()) return;
    looks.set(e.pointerId, { x: e.clientX, y: e.clientY });
    lookZone.setPointerCapture(e.pointerId);
    if (looks.size === 2) pinchDistance = spread();
  });

  lookZone.addEventListener('pointermove', (e) => {
    const previous = looks.get(e.pointerId);
    if (!previous) return;
    e.preventDefault();

    if (looks.size >= 2) {
      // Two fingers: pinch to zoom, and do not also swing the camera, which
      // would make every zoom yank the view sideways.
      looks.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const next = spread();
      if (pinchDistance > 0) hooks.zoom((pinchDistance - next) * 2.2);
      pinchDistance = next;
      return;
    }

    hooks.look(
      (e.clientX - previous.x) * LOOK_SPEED,
      (e.clientY - previous.y) * LOOK_SPEED,
    );
    looks.set(e.pointerId, { x: e.clientX, y: e.clientY });
  });

  const endLook = (e: PointerEvent): void => {
    looks.delete(e.pointerId);
    if (looks.size < 2) pinchDistance = 0;
  };
  lookZone.addEventListener('pointerup', endLook);
  lookZone.addEventListener('pointercancel', endLook);

  function spread(): number {
    const [a, b] = [...looks.values()];
    if (!a || !b) return 0;
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  return {
    state,
    setVisible(visible: boolean): void {
      layer.classList.toggle('hidden', !visible);
      if (!visible) {
        // Leaving the stick deflected while the layer is hidden would have the
        // avatar still walking behind an open phone.
        stickPointer = null;
        looks.clear();
        knob.style.transform = '';
        stick.classList.remove('active');
        state.forward = 0;
        state.strafe = 0;
        state.running = false;
      } else {
        state.running = runLatched;
      }
    },
    setInteractEnabled(enabled: boolean): void {
      interactBtn.classList.toggle('dim', !enabled);
    },
    setModal(open: boolean): void {
      // Clearing runs before the no-op guard, not after it. A button pressed
      // in the same frame the state settles would otherwise keep its held
      // styling forever: the guard returns early because the state did not
      // actually change, and the clear never happens.
      for (const el of layer.querySelectorAll('.down')) el.classList.remove('down');
      if (layer.classList.contains('modal') === open) return;
      layer.classList.toggle('modal', open);
      if (open) {
        stickPointer = null;
        looks.clear();
        knob.style.transform = '';
        stick.classList.remove('active');
        state.forward = 0;
        state.strafe = 0;
        state.running = false;
      } else {
        state.running = runLatched;
      }
    },
    dispose(): void {
      layer.remove();
    },
  };
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
